import { create } from 'zustand';

/**
 * Global queue refresh bus.
 *
 * Any successful write (POST / PATCH / PUT / DELETE) from the axios layer
 * calls `bumpQueues()`. Every `useAutoRefresh` consumer subscribes to
 * `lastBump` and re-runs its `onRefresh` callback whenever the tick changes,
 * so a cancel / process / status update in one queue automatically refreshes
 * whichever queue (and its tab badges) the user is currently looking at.
 *
 * The blast radius is intentionally narrow: the SPA only mounts one queue at
 * a time, so a bump fires the active queue's existing refresh path — not
 * every queue in the app. No new endpoints, no new contracts, no cache to
 * maintain. Drop a `bumpQueues()` call anywhere on a hot path that should
 * surface to the user immediately (the interceptor already covers the common
 * case for free).
 *
 * Read once at the top of an effect with `useQueueRefreshBus((s) => s.lastBump)`
 * so the component re-renders only on the tick, never on the noop reads.
 */
interface QueueRefreshBusState {
  lastBump: number;
  bump: () => void;
}

export const useQueueRefreshBus = create<QueueRefreshBusState>((set) => ({
  lastBump: 0,
  bump: () => set({ lastBump: Date.now() }),
}));

/**
 * Imperative bump for non-React call sites (axios interceptors, service
 * helpers). The hook variant is for React subscribers.
 *
 * Leading + trailing debounce (~400ms window): the FIRST write fires the bump
 * immediately, so the common single-write case (a status change, cancel, process
 * action) refetches with no added latency. Any further writes within the window
 * coalesce into ONE trailing bump when it closes. So:
 *   - single isolated write  → 1 immediate refetch
 *   - burst (bulk action firing N PATCHes, multi-save wizard) → 1 immediate
 *     refetch + 1 trailing refetch that captures the final state,
 * instead of N back-to-back full-list refetches.
 */
const BUMP_WINDOW_MS = 400;
let bumpCooldown: ReturnType<typeof setTimeout> | null = null;
let bumpTrailingPending = false;
export const bumpQueues = (): void => {
  // Inside an open window: a leading bump already fired — coalesce into one trailing bump.
  if (bumpCooldown !== null) {
    bumpTrailingPending = true;
    return;
  }
  // Leading edge: fire immediately, then open the coalescing window.
  useQueueRefreshBus.getState().bump();
  const closeWindow = (): void => {
    if (bumpTrailingPending) {
      // Writes landed during the window — fire one trailing bump for the final
      // state, then keep the window open to absorb any continuing burst.
      bumpTrailingPending = false;
      useQueueRefreshBus.getState().bump();
      bumpCooldown = setTimeout(closeWindow, BUMP_WINDOW_MS);
    } else {
      bumpCooldown = null;
    }
  };
  bumpCooldown = setTimeout(closeWindow, BUMP_WINDOW_MS);
};
